import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { WarehouseMatchList } from '@views/WarehouseDetails/WarehouseDetails';
import { DocumentTextIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context';
import { useAuthHttp } from '../../hooks';
import { CONTRACTED_MATCH_LIST } from '../../constants';
import { businessProfileAtom } from '../../store/jotai';
import { UserRoleService } from '../../services';
import { PageHeader } from '../../tailwindComponents/PageHeader';

type LegalDocument = {
  documentId: string;
  createdAt: string;
  updatedAt: string;
  slug: string;
  documentName: string;
  name: string;
  completedAt: string;
  status: string;
  url: string;
};

export function LegalDocumentsHub() {
  const { user } = useAuth();
  const { isWarehouseManager } = UserRoleService();
  const authHttp = useAuthHttp();
  const [serviceAgreements, setServiceAgreements] = useState<LegalDocument[]>(
    []
  );
  const businessProfile = useAtomValue(businessProfileAtom);

  useEffect(() => {
    const fetchWarehouseServiceAgreements = async () => {
      try {
        const { data: contractedMatches } =
          await authHttp.post<WarehouseMatchList>(
            CONTRACTED_MATCH_LIST,
            {},
            {
              headers: { 'Content-Type': 'application/json' },
            }
          );

        if (contractedMatches) {
          setServiceAgreements(
            contractedMatches.matches.flatMap(
              (contractedWarehouse) => contractedWarehouse.match.legalDocuments
            )
          );
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchWarehouseServiceAgreements();
  }, []);

  const hasAgreedToWarehouseTandCs = Boolean(
    businessProfile?.warehouseTandCAgreement?.hasAgreed
  );

  const showWarehouseTandCAgreement = isWarehouseManager(user)
    ? hasAgreedToWarehouseTandCs
    : serviceAgreements.length >= 1;

  const pages = [
    { name: 'Legal Documents', href: '/legal-documents', current: true },
  ];

  return (
    <>
      <PageHeader
        header='Legal Documents Hub'
        subHeader='View and manage your legal agreements and contracts'
        pages={pages}
      />
      <main className='flex flex-col gap-6 p-4 pb-16 lg:px-8 lg:pb-24'>
        {user?.roles.includes('BusinessUserManager') && (
          <>
            {/* Platform Agreements Card */}
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
              <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
                <div className='flex items-center gap-3'>
                  <LinkIcon className='h-5 w-5 text-gray-400' />
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Platform Agreements
                  </h2>
                </div>
              </div>
              <div className='p-6 space-y-4'>
                <div className='flex items-center gap-2'>
                  <a
                    href='https://www.flox.is/platform-terms'
                    target='_blank'
                    rel='noreferrer'
                    className='text-sm text-blue-600 hover:text-blue-800 hover:underline'
                  >
                    Platform Terms and Conditions
                  </a>
                  <span className='text-sm text-gray-500'>
                    - accepted by {user.name}
                  </span>
                </div>
                {showWarehouseTandCAgreement && (
                  <div className='flex items-center gap-2'>
                    <a
                      href='https://www.flox.is/standard-conditions-of-warehousing-services'
                      className='text-sm text-blue-600 hover:text-blue-800 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Standard Conditions of Warehousing Services
                    </a>
                    {isWarehouseManager(user) ? (
                      <span className='text-sm text-gray-500'>
                        - accepted{' '}
                        {(() => {
                          const date = new Date(
                            businessProfile?.warehouseTandCAgreement?.agreementDate
                          );
                          return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}/${date.getFullYear()}`;
                        })()}{' '}
                        by{' '}
                        {businessProfile?.warehouseTandCAgreement?.agreedByName}
                      </span>
                    ) : (
                      <span className='text-sm text-gray-500'>
                        - accepted by {serviceAgreements[0].name}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Price Schedules Card */}
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
              <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
                <div className='flex items-center gap-3'>
                  <DocumentTextIcon className='h-5 w-5 text-gray-400' />
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Warehousing Service Price Schedules
                  </h2>
                </div>
              </div>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th
                        scope='col'
                        className='py-3.5 pl-6 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        #
                      </th>
                      <th
                        scope='col'
                        className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Service Provider
                      </th>
                      <th
                        scope='col'
                        className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Signed Date
                      </th>
                      <th
                        scope='col'
                        className='py-3.5 pl-3 pr-6 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Signed By
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 bg-white'>
                    {serviceAgreements.map((agreement, i) => (
                      <tr
                        key={agreement.documentName}
                        className='hover:bg-gray-50 transition-colors'
                      >
                        <td className='whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-gray-900'>
                          {i + 1}
                        </td>
                        <td className='whitespace-nowrap px-3 py-4 text-sm'>
                          <a
                            href={agreement.url}
                            target='_blank'
                            rel='noreferrer'
                            className='text-blue-600 hover:text-blue-800 hover:underline'
                          >
                            {agreement.documentName}
                          </a>
                        </td>
                        <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                          {agreement.completedAt
                            ? (() => {
                                const date = new Date(agreement.completedAt);
                                return `${date
                                  .getDate()
                                  .toString()
                                  .padStart(2, '0')}/${(date.getMonth() + 1)
                                  .toString()
                                  .padStart(2, '0')}/${date.getFullYear()}`;
                              })()
                            : 'N/A'}
                        </td>
                        <td className='whitespace-nowrap py-4 pl-3 pr-6 text-sm text-gray-500'>
                          {agreement.name}
                        </td>
                      </tr>
                    ))}
                    {serviceAgreements.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className='py-8 text-center text-sm text-gray-500'
                        >
                          No service agreements found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </>
  );
}
